import { useMemo } from 'react';
import { Link } from 'react-router';

import { useLanguage } from '../../contexts/language';

import styles from './style.module.css';

// This week's records at a glance, Monday to Sunday: a bar per day coloured
// by completeness — never by what was recorded — green complete, yellow
// partial, white no entry, a dashed outline for the days to come, with the
// day's letter under it and today's in bold. Each day also says its state in
// words for screen readers, so the colour never carries the meaning alone.
//
// A day that has come opens the Diary on that day (#150); a day still to
// come has nothing to open and stays as it is.
export function WeekRecords({ days, today }) {
  const { text, language } = useLanguage();
  const letter = useMemo(function() {
    return new Intl.DateTimeFormat(language, { weekday: 'narrow', timeZone: 'UTC' });
  }, [ language ]);
  const long = useMemo(function() {
    return new Intl.DateTimeFormat(language, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
  }, [ language ]);

  const complete = useMemo(function() {
    return days.filter(function({ completeness }) {
      return completeness === 'COMPLETE';
    }).length;
  }, [ days ]);
  const partial = useMemo(function() {
    return days.filter(function({ completeness }) {
      return completeness === 'PARTIAL';
    }).length;
  }, [ days ]);

  const cells = useMemo(function() {
    return days.map(function({ date, completeness }) {
      const at = new Date(`${ date }T00:00:00Z`);
      let bar = `${ styles.bar } ${ styles[ completeness ] }`;
      let state = text.completeness[ completeness ];
      let label = styles.letter;

      if (date > today) {
        bar = `${ styles.bar } ${ styles.future }`;
        state = text.toCome;
      }

      if (date === today) {
        label = `${ styles.letter } ${ styles.current }`;
      }

      const content = [
        <span key='bar' className={ bar } aria-hidden='true'></span>,
        <span key='letter' className={ label } aria-hidden='true'>{ letter.format(at) }</span>,
        <span key='state' className={ styles.hidden }>{ `${ long.format(at) }: ${ state }` }</span>
      ];

      let cell = <Link className={ `${ styles.cell } ${ styles.open }` } to={ `/diary?day=${ date }` }>{ content }</Link>;

      if (date > today) {
        cell = <div className={ styles.cell }>{ content }</div>;
      }

      return <li key={ date } className={ styles.day }>{ cell }</li>;
    });
  }, [ days, today, text, letter, long ]);

  return (
    <div className={ styles.pane }>
      <h2 className={ styles.label }>{ text.dailyRecords }</h2>

      <div className={ styles.row }>
        <span className={ styles.week }>{ text.thisWeek }</span>
        <span className={ styles.counts }>{ text.weekCounts.replace('{complete}', complete).replace('{partial}', partial) }</span>
      </div>

      <ol className={ styles.days }>{ cells }</ol>
    </div>
  );
}
